import { z } from 'zod';

import { ProjectDto, SourceCode, SourceLanguage, Workspace } from './project.js';

/**
 * Live sessions (plan §4.7, ADR 0009). The link decides the role: the 6-character view code
 * gives a reader, `VIEWCODE-editsecret` an editor. Inside a room one participant at a time holds
 * control and may change the blocks.
 */
export const VIEW_CODE = /^[A-Z0-9]{6}$/;
export const EDIT_CODE = /^[A-Z0-9]{6}-[A-Za-z0-9]{8}$/;

export const SessionRole = z.enum(['reader', 'editor']);
export type SessionRole = z.infer<typeof SessionRole>;

export const SessionInfo = z.object({
  id: z.string(),
  viewCode: z.string(),
  startedAt: z.string(),
  closed: z.boolean(),
  /** A class session (ADR 0016): only that class's members may watch with the view code. */
  classId: z.string().nullable().default(null),
});
export type SessionInfo = z.infer<typeof SessionInfo>;

/** POST /api/projects/:id/sessions */
export const CreateSessionResponse = z.object({
  session: SessionInfo,
  viewCode: z.string(),
  editCode: z.string(),
});
export type CreateSessionResponse = z.infer<typeof CreateSessionResponse>;

/** GET /api/sessions/:code */
export const SessionLookupResponse = z.object({
  role: SessionRole,
  session: SessionInfo,
  project: ProjectDto,
  className: z.string().nullable().default(null),
});
export type SessionLookupResponse = z.infer<typeof SessionLookupResponse>;

export const Participant = z.object({
  clientId: z.string(),
  name: z.string(),
  role: SessionRole,
  /** True for the project's owner (the browser that created it). */
  owner: z.boolean(),
  /** Their chosen avatar colour (an unlocked reward), resolved on the server. */
  color: z.string().nullable(),
});
export type Participant = z.infer<typeof Participant>;

export const ActivityEntry = z.object({
  at: z.string(),
  clientId: z.string(),
  name: z.string(),
  kind: z.enum(['run', 'tutor', 'block']),
  /** run: status + output summary; tutor: mode (+ hint level); block: block type. */
  detail: z.record(z.string(), z.string()),
});
export type ActivityEntry = z.infer<typeof ActivityEntry>;

export const RunMirror = z.object({
  clientId: z.string(),
  name: z.string(),
  stdin: z.string().max(10_000),
  /** Console lines as shown to the runner (already formatted, trace lines included). */
  lines: z
    .array(z.object({ kind: z.enum(['out', 'trace', 'error']), text: z.string().max(500) }))
    .max(300),
  status: z.enum(['finished', 'error', 'step-limit', 'stopped']),
  at: z.string(),
});
export type RunMirror = z.infer<typeof RunMirror>;

const DisplayName = z.string().trim().min(1).max(40);

export const ClientMessage = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('join'),
    code: z.string().max(20),
    clientId: z.string().min(8).max(64),
    name: DisplayName,
    /** Lets the project's owner edit even through the view link. */
    ownerToken: z.string().optional(),
    /** The student's calendar date, for the live-session reward. */
    localDate: z.string().max(10).optional(),
  }),
  z.object({
    type: z.literal('update'),
    rev: z.number().int().nonnegative(),
    workspace: Workspace,
    /**
     * The text the workspace was parsed from (ADR 0012). It travels with the workspace so the
     * two can never disagree; absent means the blocks were edited and the stored text stands.
     */
    source: SourceCode.nullable().optional(),
    sourceLang: SourceLanguage.nullable().optional(),
  }),
  z.object({ type: z.literal('take_control') }),
  z.object({ type: z.literal('request_control') }),
  z.object({ type: z.literal('grant_control'), clientId: z.string() }),
  z.object({
    type: z.literal('activity'),
    kind: ActivityEntry.shape.kind,
    detail: z.record(z.string().max(40), z.string().max(200)),
  }),
  z.object({
    type: z.literal('run'),
    run: RunMirror.omit({ clientId: true, name: true, at: true }),
  }),
  z.object({ type: z.literal('chat'), text: z.string().trim().min(1).max(500) }),
  z.object({ type: z.literal('ping') }),
]);
export type ClientMessage = z.infer<typeof ClientMessage>;

export const ServerMessage = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('joined'),
    role: SessionRole,
    clientId: z.string(),
    session: SessionInfo,
    project: ProjectDto,
    participants: z.array(Participant),
    controllerId: z.string().nullable(),
    activity: z.array(ActivityEntry),
    lastRun: RunMirror.nullable(),
    lastChangeAt: z.string().nullable(),
  }),
  z.object({
    type: z.literal('changed'),
    rev: z.number(),
    workspace: Workspace,
    /** The stored text behind those blocks, so a reader follows what was typed, not a rebuild. */
    source: SourceCode.nullable(),
    sourceLang: SourceLanguage.nullable(),
    fromClientId: z.string().nullable(),
    at: z.string(),
  }),
  /** Your update was accepted and stored as revision `rev`. */
  z.object({ type: z.literal('saved'), rev: z.number() }),
  z.object({
    type: z.literal('presence'),
    participants: z.array(Participant),
    controllerId: z.string().nullable(),
  }),
  z.object({ type: z.literal('control_requested'), clientId: z.string(), name: z.string() }),
  z.object({ type: z.literal('activity'), entry: ActivityEntry }),
  z.object({ type: z.literal('run'), run: RunMirror }),
  z.object({
    type: z.literal('chat'),
    fromClientId: z.string(),
    name: z.string(),
    text: z.string(),
    at: z.string(),
  }),
  z.object({ type: z.literal('ended') }),
  z.object({
    type: z.literal('error'),
    reason: z.enum([
      'not-found',
      'ended',
      'not-allowed',
      'stale',
      'invalid',
      'not-joined',
      /** A class session and the joiner is not a (logged-in) member of that class. */
      'class-only',
    ]),
  }),
  z.object({ type: z.literal('pong') }),
]);
export type ServerMessage = z.infer<typeof ServerMessage>;
